import styles from './Opinions.module.scss';
import 'swiper/css';

import { Loader, Modal } from '@/components';
import { Button } from '@/components/Button';
import { useGetLanguage } from '@/hooks';
import { useHapticFeedback } from '@/hooks/useHapticFeedback';
import { useNavigation } from '@/hooks/useNavigation';
import { NOTIFICATION_TYPE } from '@/modules/notification/types';
import { OpinionsApi, useOpinionContext } from '@/modules/opinions';
import type { OpinionPoll, OpinionPollVoted } from '@/modules/opinions';
import { OpinionSlide } from '@/modules/opinions/components';
import { PAGE } from '@/modules/page/routes';
import { SonarReturnStatus } from '@/shared/types/adsonar';
import { useStore } from '@/store';
import { analytics } from '@/utils/analytics';
import { HELIKA_EVENT_TYPE } from '@/utils/helika';
import { isB3LauncherApp } from '@/utils/isB3LauncherApp';
import 'swiper/css/virtual';
import { Manipulation, Virtual } from 'swiper/modules';
import type { Swiper as SwiperType } from 'swiper/types';

import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Swiper, SwiperSlide } from 'swiper/react';

import { OpinionSlideProvider } from './context';

type OpinionsProps = {
  data: OpinionPoll[];
  activeSlideIndex?: number;
  fetchNextPage?: () => void;
  showAds?: boolean;
};

export const Opinions = ({
  data,
  activeSlideIndex,
  fetchNextPage,
  showAds = false,
}: OpinionsProps) => {
  const opinion = data[activeSlideIndex ?? 0];

  if (!opinion)
    return (
      <Loader
        screenStyles={{
          height: '100vh',
          alignItems: 'flex-start',
          paddingTop: '200px',
        }}
      />
    );

  return (
    <OpinionSlideProvider opinion={opinion}>
      <OpinionPolls
        data={data}
        activeSlideIndex={activeSlideIndex}
        fetchNextPage={fetchNextPage}
        showAds={showAds}
      />
    </OpinionSlideProvider>
  );
};

export const OpinionPolls = ({
  data,
  activeSlideIndex,
  fetchNextPage,
  showAds = false,
}: OpinionsProps) => {
  const { currentPage, params } = useNavigation();
  const navigate = useNavigate();
  const [activeSlideId, setActiveSlideId] = useState<string | null>(null);
  const sliderRef = useRef<SwiperType>();
  const lang = useGetLanguage();
  const { setOpinion, setIsVoted, setAnswerItemId, setSlidesCount } =
    useOpinionContext();
  const {
    opinionsStore: { opinionsVotedCount, clearOpinionsVotedCount },
    accountStore: { accountData },
    notificationStore: { setNotificationType, setNotificationMessage },
    walletStore: { mappedAssets, getJettonBalances },
  } = useStore();
  const isAuthenticated = Boolean(accountData?._id);
  const [isAdModalVisible, setIsAdModalVisible] = useState(false);
  const { t } = useTranslation();
  const { triggerHapticFeedback } = useHapticFeedback();
  const stableInitialSlide = useRef(activeSlideIndex ?? 0);
  const queryClient = useQueryClient();

  useEffect(() => {
    if (data?.length) setSlidesCount(data.length);
  }, [data]);

  useEffect(() => {
    if (activeSlideId && currentPage === PAGE.OPINION_ID) {
      const newPath = `/opinions/${activeSlideId}`;
      if (location.pathname !== newPath) {
        navigate(newPath, { replace: true });
      }
    }

    if (activeSlideId && currentPage === PAGE.TOURNAMENT_ID_OPINION_ID) {
      const newPath = `/tournaments/${params.id!.toString()}/opinions/${activeSlideId}`;
      if (location.pathname !== newPath) {
        navigate(newPath, { replace: true });
      }
    }

    if (activeSlideId && currentPage === PAGE.PROFILE_ID_OPINION_ID) {
      const newPath = `/profile/${params.id!.toString()}/opinions/${activeSlideId}`;
      if (location.pathname !== newPath) {
        navigate(newPath, { replace: true });
      }
    }

    if (
      sliderRef?.current &&
      fetchNextPage &&
      sliderRef.current.activeIndex === data.length - 1
    ) {
      fetchNextPage();
      sliderRef.current.update();
    }

    if (activeSlideId) {
      analytics.sendEvent({
        event_type: HELIKA_EVENT_TYPE.UI,
        event: {
          event_sub_type: 'opinion_opened',
          event_details: {
            event_label: 'Opinion opened',
            event_action: 'open',
          },
          opinion_id: activeSlideId,
        },
      });
    }
  }, [activeSlideId]);

  if (!data.length) return <Loader />;

  const showRewardedAd = () => {
    triggerHapticFeedback('medium');
    setIsAdModalVisible(false);
    window.Sonar.show({
      adUnit: '500vote',
      loader: true,
      onReward: async () => {
        try {
          clearOpinionsVotedCount();
          await OpinionsApi.requestAdReward();
          getJettonBalances(); // to show confetti
        } catch (error) {
          // @ts-ignore
          const errorMessage = error?.response?.data?.content;
          if (errorMessage) {
            setNotificationMessage(errorMessage);
            setNotificationType(NOTIFICATION_TYPE.COMMON);
          }
        }
      },
    }).then((result: { status: SonarReturnStatus; message?: string }) => {
      if (result.status === 'error') {
        console.error('Ad failed to show:', result.message);
      } else {
        console.log('Ad status:', result.status);
      }
    });
  };

  const onSlideChangeTransitionEnd = (swiper: SwiperType) => {
    handleSlideChange(data[swiper.activeIndex]);

    if (
      !isB3LauncherApp() &&
      isAuthenticated &&
      showAds &&
      import.meta.env.VITE_ADSONAR_APP_ID &&
      import.meta.env.VITE_ADSONAR_AD_EVERY_N_OPINIONS
    ) {
      if (
        opinionsVotedCount &&
        opinionsVotedCount %
          Number(import.meta.env.VITE_ADSONAR_AD_EVERY_N_OPINIONS) ===
          0
      ) {
        setTimeout(() => {
          setIsAdModalVisible(true);
        }, 250);
      }
    }
  };

  const lastSlideIdRef = useRef<string | null>(null);

  const handleSlideChange = async (slide: OpinionPoll) => {
    if (!slide?._id || lastSlideIdRef.current === slide._id) return;
    lastSlideIdRef.current = slide._id;
    setActiveSlideId(slide._id);

    if (currentPage !== PAGE.TOURNAMENT_ID_OPINION_ID) {
      try {
        const opinion = await queryClient.fetchQuery({
          queryKey: [OpinionsApi.KEY, 'getOpinionById', slide._id, lang],
          queryFn: () => OpinionsApi.getOpinionsById(slide._id, lang),
          staleTime: 1000 * 60 * 5, // 5 minutes
        });

        setOpinion(opinion);
        setIsVoted(false);
        setAnswerItemId(null);
      } catch (e) {
        console.error('Failed to fetch opinion:', e);
      }
    } else {
      setOpinion(slide as unknown as OpinionPollVoted);
      setIsVoted(false);
      setAnswerItemId(null);
    }
  };

  useEffect(() => {
    const initialSlide = data[stableInitialSlide.current];
    if (initialSlide) {
      handleSlideChange(initialSlide);
    }
  }, []);

  const handleCloseAdModal = () => {
    clearOpinionsVotedCount();
    setIsAdModalVisible(false);
  };

  return (
    <Swiper
      className={styles.opinions}
      direction="vertical"
      speed={500}
      grabCursor
      watchOverflow
      followFinger
      passiveListeners
      noSwiping
      noSwipingSelector=".swiper-no-swiping"
      modules={[Manipulation, Virtual]}
      initialSlide={stableInitialSlide.current}
      onSwiper={(swiper) => {
        sliderRef.current = swiper;
      }}
      virtual
      onSlideChangeTransitionEnd={onSlideChangeTransitionEnd}
    >
      {data.map((item, index) => (
        <SwiperSlide
          key={item._id}
          virtualIndex={index}
          className={styles.slide}
        >
          <OpinionSlide item={item} />

          <Modal
            isVisible={isAdModalVisible}
            onClose={handleCloseAdModal}
            minHeight="auto"
          >
            <div className={styles.modalText}>
              {t('Watch a short ad and earn 500 Vote!')}
            </div>
            <Button gradient onClick={showRewardedAd}>
              {t('Get')} <mappedAssets.vote.smallIcon /> 500{' '}
              {mappedAssets.vote.ticker}
            </Button>
            <br />
          </Modal>
        </SwiperSlide>
      ))}
    </Swiper>
  );
};
